import { Injectable } from '@angular/core';
import { DataEntry } from '../models/indicator-data';
import { Scale } from '../models/scale';
import { ScaleMode } from './grid-widget-config.service';

@Injectable({
  providedIn: 'root',
})
export class ScaleService {
  /**
   * Generates a set of scales based on the given data, colors, and mode.
   *
   * @param {DataEntry} data - The input dataset containing key-value pairs.
   * @param {string[]} colors - An array of color strings to use for the scales.
   * @param {ScaleMode} [mode='width'] - The mode of scale generation, either 'width' or other specified modes.
   * @param {number} [numberOfScales] - Optional parameter specifying the desired number of scales.
   * @param {Scale[]} [additionalScales] - Optional parameter containing additional pre-defined scales.
   * @return {Scale[]} An array of generated scales based on the input parameters.
   */
  generateScales(
    data: DataEntry,
    colors: string[],
    mode: ScaleMode = 'width',
    numberOfScales?: number,
    additionalScales?: Scale[]
  ): Scale[] {
    const values = Object.values(data).filter((v): v is number => v !== null);

    if (additionalScales) {
      values.push(additionalScales[0].from);
      values.push(additionalScales[additionalScales.length - 1].to);
    }

    let numScales =
      numberOfScales && numberOfScales <= colors.length
        ? numberOfScales
        : colors.length;
    if (numScales > values.length) {
      numScales = values.length;
    }

    return mode === 'width'
      ? this.getScalesByWidthMode(values, numScales)
      : this.getScalesByQuantiles(values, numScales);
  }

  /**
   * Generates a set of scales based on the provided values and width mode.
   *
   * @param {number[]} values - An array of numeric values used to determine the range of the scales.
   * @param {number} numberOfScales - The number of scales to divide the range into.
   * @return {Scale[]} An array of Scale objects representing the calculated scales.
   */
  getScalesByWidthMode(values: number[], numberOfScales: number): Scale[] {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const numScales = numberOfScales || numberOfScales;
    const step = (max - min) / numScales;
    const scales: Scale[] = [];
    for (let i = 0; i < numScales; i++) {
      const scale = {
        from: min + i * step,
        to: i === numScales ? max : min + (i + 1) * step,
        text: '',
        //text: `${min + i * step} - ${i === numScales ? max : min + (i + 1) * step}`,
      };
      scales.push(scale);
    }
    return scales;
  }

  /**
   * Calculates scales based on quantiles to ensure even distribution of values across scales.
   * @param values - Array of numbers to be used for scale calculation.
   * @param numberOfScales - The desired number of scales.
   * @returns Array of scales with evenly distributed values.
   */
  getScalesByQuantiles(values: number[], numberOfScales: number): Scale[] {
    if (values.length === 0 || numberOfScales < 1) {
      return [];
    }

    const sortedValues = [...values].sort((a, b) => a - b); // Sort values in ascending order
    const scales: Scale[] = [];

    // Determine the quantile step
    const step = sortedValues.length / numberOfScales;

    for (let i = 0; i < numberOfScales; i++) {
      const fromIndex = Math.floor(i * step);

      const from = sortedValues[fromIndex];
      const to =
        i === numberOfScales - 1
          ? sortedValues[sortedValues.length - 1] // Ensure the last scale ends at the max value
          : sortedValues[Math.floor((i + 1) * step)]; // Make the "to" of this equal to the "from" of the next

      // Avoid duplicate ranges by ensuring `to` is greater than `from`
      scales.push({
        from,
        to: Math.max(to, from),
        text: '',
      });
    }

    return scales;
  }

  /**
   * Determines the color associated with a given value based on defined scales and corresponding colors.
   *
   * @param {number} value - The numeric value to evaluate against the scales.
   * @param {Scale[]} scales - An array of scale objects, where each scale has `from` and `to` properties defining its range.
   * @param {string[]} colors - An array of colors, where each index corresponds to the color for the respective scale.
   * @return {string} The color associated with the provided value. If no scale matches, a default color (`#444444`) is returned.
   */
  getColor(value: number, scales: Scale[], colors: string[]): string {
    for (let i = 0; i < scales.length; i++) {
      if (
        value >= scales[i].from &&
        (value < scales[i].to || i === scales.length - 1)
      ) {
        return colors[i];
      }
    }
    return '#444444'; // Default color if no match
  }

  /**
   * Retrieves the label corresponding to a given value based on the provided scales.
   *
   * @param {number} value - The numeric value for which the scale label is to be determined.
   * @param {Scale[]} scales - An array of Scale objects, each containing `from`, `to`, and `text` properties.
   * @return {string | null} - The label associated with the given value, or null if no matching scale is found.
   */
  getScaleLabel(value: number, scales: Scale[]): string | null {
    for (let i = 0; i < scales.length; i++) {
      if (
        value >= scales[i].from &&
        (value < scales[i].to || i === scales.length - 1)
      ) {
        return scales[i].text || null;
      }
    }
    return null; // Default color if no match
  }
}
